import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('wizbang_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('wizbang_token');
      localStorage.removeItem('wizbang_user');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => API.post('/auth/login', data);
export const register = (data) => API.post('/auth/register', data);
export const getMe = () => API.get('/auth/me');

// Team
export const getTeam = () => API.get('/team');
export const createTeamMember = (data) => API.post('/team', data);
export const updateTeamMember = (id, data) => API.put(`/team/${id}`, data);
export const deleteTeamMember = (id) => API.delete(`/team/${id}`);

// Blog
export const getBlogs = (publishedOnly = true) => API.get(`/blog?published_only=${publishedOnly}`);
export const getBlogBySlug = (slug) => API.get(`/blog/${slug}`);
export const createBlog = (data) => API.post('/blog', data);
export const updateBlog = (id, data) => API.put(`/blog/${id}`, data);
export const deleteBlog = (id) => API.delete(`/blog/${id}`);

// Clients
export const getClients = () => API.get('/clients');
export const createClient = (data) => API.post('/clients', data);
export const updateClient = (id, data) => API.put(`/clients/${id}`, data);
export const deleteClient = (id) => API.delete(`/clients/${id}`);

// Appointments
export const getAppointments = () => API.get('/appointments');
export const createAppointment = (data) => API.post('/appointments', data);
export const updateAppointmentStatus = (id, status) => API.put(`/appointments/${id}/status?status=${status}`);
export const deleteAppointment = (id) => API.delete(`/appointments/${id}`);

// Contacts
export const getContacts = () => API.get('/contacts');
export const createContact = (data) => API.post('/contacts', data);
export const markContactRead = (id) => API.put(`/contacts/${id}/read`);
export const deleteContact = (id) => API.delete(`/contacts/${id}`);

// FAQ
export const getFAQs = (activeOnly = true) => API.get(`/faq?active_only=${activeOnly}`);
export const createFAQ = (data) => API.post('/faq', data);
export const updateFAQ = (id, data) => API.put(`/faq/${id}`, data);
export const deleteFAQ = (id) => API.delete(`/faq/${id}`);

// Chat
export const sendChatMessage = (data) => API.post('/chat', data);

// Stats
export const getStats = () => API.get('/stats');
export const createStat = (data) => API.post('/stats', data);
export const updateStat = (id, data) => API.put(`/stats/${id}`, data);
export const deleteStat = (id) => API.delete(`/stats/${id}`);

// Careers
export const getCareers = (activeOnly = true) => API.get(`/careers?active_only=${activeOnly}`);
export const getCareer = (id) => API.get(`/careers/${id}`);
export const createCareer = (data) => API.post('/careers', data);
export const updateCareer = (id, data) => API.put(`/careers/${id}`, data);
export const deleteCareer = (id) => API.delete(`/careers/${id}`);

// Case Studies
export const getCaseStudies = (params = {}) => {
  const query = new URLSearchParams();
  if (params.publishedOnly !== undefined) query.append('published_only', params.publishedOnly);
  if (params.featuredOnly) query.append('featured_only', 'true');
  if (params.service) query.append('service', params.service);
  const qs = query.toString();
  return API.get(`/case-studies${qs ? `?${qs}` : ''}`);
};
export const getCaseStudyBySlug = (slug) => API.get(`/case-studies/${slug}`);
export const createCaseStudy = (data) => API.post('/case-studies', data);
export const updateCaseStudy = (id, data) => API.put(`/case-studies/${id}`, data);
export const deleteCaseStudy = (id) => API.delete(`/case-studies/${id}`);

// ============== ERP APIs ==============

// ERP Clients
export const getERPClients = (activeOnly = false) => API.get(`/erp/clients?active_only=${activeOnly}`);
export const getERPClient = (id) => API.get(`/erp/clients/${id}`);
export const createERPClient = (data) => API.post('/erp/clients', data);
export const updateERPClient = (id, data) => API.put(`/erp/clients/${id}`, data);
export const deleteERPClient = (id) => API.delete(`/erp/clients/${id}`);

// Income Categories
export const getIncomeCategories = () => API.get('/erp/income-categories');
export const createIncomeCategory = (data) => API.post('/erp/income-categories', data);
export const deleteIncomeCategory = (id) => API.delete(`/erp/income-categories/${id}`);

// Expense Categories
export const getExpenseCategories = () => API.get('/erp/expense-categories');
export const createExpenseCategory = (data) => API.post('/erp/expense-categories', data);
export const deleteExpenseCategory = (id) => API.delete(`/erp/expense-categories/${id}`);

// Income
export const getIncome = (month, year) => {
  let url = '/erp/income';
  const params = [];
  if (month) params.push(`month=${month}`);
  if (year) params.push(`year=${year}`);
  if (params.length) url += '?' + params.join('&');
  return API.get(url);
};
export const createIncome = (data) => API.post('/erp/income', data);
export const deleteIncome = (id) => API.delete(`/erp/income/${id}`);

// Expense
export const getExpenses = (month, year) => {
  let url = '/erp/expense';
  const params = [];
  if (month) params.push(`month=${month}`);
  if (year) params.push(`year=${year}`);
  if (params.length) url += '?' + params.join('&');
  return API.get(url);
};
export const createExpense = (data) => API.post('/erp/expense', data);
export const deleteExpense = (id) => API.delete(`/erp/expense/${id}`);

// Bank Balance (auto-calculated)
export const getBankBalance = () => API.get('/erp/bank-balance');

// ERP Summary
export const getERPSummary = (month, year) => {
  let url = '/erp/summary';
  const params = [];
  if (month) params.push(`month=${month}`);
  if (year) params.push(`year=${year}`);
  if (params.length) url += '?' + params.join('&');
  return API.get(url);
};
